import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import {
  HORIZONTAL_POSITION_META,
  buildHorizontalSeed,
  getHorizontalVariant
} from "../content/horizontalNumerologica";

const formatDate = (value) => {
  if (!value) return "—";
  const formatter = new Intl.DateTimeFormat("es-MX", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC"
  });

  try {
    if (typeof value === "string") {
      const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        const [, yearStr, monthStr, dayStr] = match;
        const [year, month, day] = [yearStr, monthStr, dayStr].map(Number);
        const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
        return formatter.format(date);
      }
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return formatter.format(date);
  } catch {
    return value;
  }
};

const InfoBlock = ({ message, icon = "ℹ️", actionLabel, onAction }) => (
  <div className="bg-white/80 border border-[#d4af37]/60 rounded-2xl p-6 text-center shadow-sm">
    <div className="text-3xl mb-2">{icon}</div>
    <p className="text-sm text-gray-700 mb-3">{message}</p>
    {actionLabel && onAction && (
      <button
        onClick={onAction}
        className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold bg-morado text-white hover:shadow-lg transition"
      >
        {actionLabel}
      </button>
    )}
  </div>
);

const POSITION_COLORS = {
  A: {
    chip: "bg-rose-100 text-rose-700",
    badge: "bg-rose-600",
    border: "border-rose-200",
    surface: "bg-rose-50/50"
  },
  B: {
    chip: "bg-morado/10 text-morado",
    badge: "bg-morado",
    border: "border-morado/20",
    surface: "bg-morado/5"
  },
  C: {
    chip: "bg-emerald-100 text-emerald-700",
    badge: "bg-emerald-600",
    border: "border-emerald-200",
    surface: "bg-emerald-50/50"
  },
  D: {
    chip: "bg-amber-100 text-amber-700",
    badge: "bg-amber-600",
    border: "border-amber-200",
    surface: "bg-amber-50/50"
  }
};

function PositionCard({
  position,
  value,
  meta,
  narrative
}) {
  const colors = POSITION_COLORS[position] || POSITION_COLORS.B;

  return (
    <div
      className={`rounded-2xl border ${colors.border} ${colors.surface} shadow-sm overflow-hidden`}
    >
      <div className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className={`w-12 h-12 rounded-full ${colors.badge} text-white font-bold text-lg flex items-center justify-center shadow-lg flex-shrink-0`}
            >
              {value ?? "—"}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[11px] font-semibold px-2 py-1 rounded-full ${colors.chip}`}>
                  {position}
                </span>
                <span className="text-xs text-gray-500">{meta.source}</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mt-1">
                {meta.icon} {meta.label}
              </h3>
              <p className="text-xs text-gray-500 mt-1">{meta.description}</p>
            </div>
          </div>
        </div>

        <div className="bg-white/80 border border-white rounded-xl p-4">
          <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
            Enfoque dinámico
          </p>
          <p className="text-sm text-gray-700 leading-relaxed mt-2">
            {narrative.emphasis}
          </p>
        </div>

        <div>
          <p className="text-sm font-semibold text-gray-800">{narrative.title}</p>
          <p className="text-sm text-gray-600 leading-relaxed mt-2">
            {narrative.summary}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {narrative.keywords.map((keyword) => (
            <span
              key={`${position}-${value}-${keyword}`}
              className="text-[11px] px-2 py-1 rounded-full bg-white border border-gray-200 text-gray-600"
            >
              {keyword}
            </span>
          ))}
        </div>

        <div className="pt-1">
          <div className="rounded-xl border border-gray-200 bg-white/90 p-4">
            <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-2">
              Lectura ampliada
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              {narrative.longText}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function HorizontalNumerologicaSection({
  profileName,
  birthDate,
  data,
  loading,
  error,
  reason,
  onRetry
}) {
  const nodes = data?.nodes || {};

  const seedKey = useMemo(
    () =>
      buildHorizontalSeed({
        profileId: data?.profileId,
        ninjaProfileId: data?.ninjaProfileId,
        birthDate: data?.birthDate || birthDate,
        profileName
      }),
    [birthDate, data?.birthDate, data?.ninjaProfileId, data?.profileId, profileName]
  );

  const positions = useMemo(() => {
    return ["A", "B", "C", "D"].map((position) => {
      const value = nodes[position.toLowerCase()] ?? null;
      const meta = HORIZONTAL_POSITION_META[position];
      const narrative =
        value === null || value === undefined
          ? null
          : getHorizontalVariant({
              position,
              number: value,
              seedKey
            });

      return {
        position,
        value,
        meta,
        narrative
      };
    });
  }, [nodes, seedKey]);

  if (loading) {
    return <InfoBlock message="Preparando tu Horizontal Numerológica..." icon="⏳" />;
  }

  if (reason) {
    return <InfoBlock message={reason} icon="💡" />;
  }

  if (error) {
    return (
      <InfoBlock
        message={error}
        icon="⚠️"
        actionLabel="Reintentar"
        onAction={onRetry}
      />
    );
  }

  if (!data) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="space-y-6"
    >
      <div
        className="rounded-2xl shadow-xl border overflow-hidden"
        style={{ borderColor: "#d4af37" }}
      >
        <div className="p-6 text-white" style={{ background: "var(--color-morado)" }}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-widest opacity-80">
                QUIÉN SOY
              </p>
              <h2 className="text-2xl font-bold">
                Las cuatro facetas de mi identidad
              </h2>
              <p className="text-sm opacity-90 mt-1">
                Perfil activo: {profileName} · Nacimiento: {formatDate(birthDate)}
              </p>
            </div>
            <div className="max-w-md text-sm text-white/90 leading-relaxed">
              Esta lectura interpreta tus posiciones <strong>A, B, C y D</strong> del
              pináculo personal como un eje de identidad: herida heredada, esencia en
              construcción, memoria del pasado y máscara social.
            </div>
          </div>
        </div>

        <div className="p-5 md:p-6 bg-white space-y-6">
          <div className="rounded-2xl border border-[#e9ddf0] bg-white/80 p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#f5ebfb] text-morado flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5" />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-morado">
                  Un mapa íntimo de tu esencia
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Aquí se revela la raíz invisible de tu identidad.
                  Estas cuatro puertas muestran la memoria que te habita,
                  la esencia que viene despertando, la sabiduría que ya traes
                  y la máscara con la que el mundo suele reconocerte.
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {positions.map(({ position, value, meta }) => {
              const colors = POSITION_COLORS[position] || POSITION_COLORS.B;
              return (
                <div
                  key={`summary-${position}`}
                  className={`rounded-2xl border ${colors.border} bg-white p-4 shadow-sm`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-11 h-11 rounded-full ${colors.badge} text-white font-bold flex items-center justify-center shadow`}
                    >
                      {value ?? "—"}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{meta.source}</p>
                      <p className="text-sm font-semibold text-gray-800">
                        {position}. {meta.shortLabel}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="grid gap-5 xl:grid-cols-2">
            {positions.map(({ position, value, meta, narrative }) => (
              <PositionCard
                key={position}
                position={position}
                value={value}
                meta={meta}
                narrative={
                  narrative || {
                    title: "Sin lectura disponible",
                    summary:
                      "Todavía no hay suficiente información para mostrar esta posición.",
                    longText:
                      "Completa el perfil y vuelve a sincronizar tu pináculo para obtener una lectura más completa.",
                    emphasis:
                      "Esta posición aún no cuenta con una interpretación lista para mostrarse.",
                    keywords: ["pendiente"]
                  }
                }
              />
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
